import React, { useMemo, useState } from 'react';
import { Alert, CircularProgress, Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AccountData } from '../../api/models/AccountData';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import type { PassScheduleSelectionDTO } from '../../api/models/PassScheduleSelectionDTO';
import PassScheduleSelector from '../../shared/passScheduleSelector/PassScheduleSelector';
import { resolveRedeemablePages } from '../../shared/passScheduleSelector/utils';
import { usePassAvailability } from '../../shared/passScheduleSelector/usePassAvailability';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';

type Props = {
  passOrderPage: PassOrderPageData;
  account: AccountData;
};

/**
 * Troubleshoot-only availability grid on the first pass step. Next is disabled while
 * troubleshooting, so the schedule step (where availability normally lives) is out of reach.
 */
const PassTroubleshootAvailability = ({ passOrderPage, account }: Props) => {
  const { t } = useTranslation();
  const hasSlots = useMemo(() => resolveRedeemablePages(passOrderPage, account).length > 0, [passOrderPage, account]);
  const { availability, loading, error } = usePassAvailability(passOrderPage, account, hasSlots);
  // Local only — picking here is just for inspecting the grid, the schedule step owns the real selection.
  const [preview, setPreview] = useState<PassScheduleSelectionDTO[]>([]);

  if (!hasSlots) {
    return null;
  }

  return (
    <Grid container flexDirection="column" gap={1.5} mt={2}>
      <Divider />
      <OrderPageTypography variant="h6" bold>
        {t('pass_schedule.troubleshoot.availabilityHeading')}
      </OrderPageTypography>
      <OrderPageTypography variant="body2" color="secondary">
        {t('pass_schedule.troubleshoot.availabilityHint')}
      </OrderPageTypography>
      {loading && <CircularProgress size={20} />}
      {error && <Alert severity="error">{t('pass_schedule.errors.availabilityLoadFailed')}</Alert>}
      {availability && (
        <PassScheduleSelector
          passOrderPage={passOrderPage}
          account={account}
          value={preview}
          onChange={setPreview}
          availability={availability}
          troubleshoot
        />
      )}
    </Grid>
  );
};

export default PassTroubleshootAvailability;
